// client/src/components/Admin/UserModal.js
import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Close from "@material-ui/icons/Close";

const modalStyles = {
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: "800px",
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    outline: "none",
    maxHeight: "90vh",
    overflow: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  modalTitle: {
    margin: 0,
    fontSize: "1.5rem",
  },
  modalBody: {
    marginBottom: "20px",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
};

const useStyles = makeStyles(modalStyles);

const UserModal = ({
  type,
  open,
  onClose,
  formData,
  formStates,
  updateFormData,
  updateFormState,
  onSubmit,
  validateEmail,
  userTypes,
}) => {
  const classes = useStyles();
  const isUpdate = type === "update";
  const title = isUpdate ? "Update User" : "Add User";
  const submitText = isUpdate ? "Update User" : "Add User";

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <div className={classes.modal}>
        <div className={classes.modalHeader}>
          <h4 className={classes.modalTitle}>{title}</h4>
          <Button justIcon onClick={onClose}>
            <Close />
          </Button>
        </div>
        <form onSubmit={onSubmit}>
          <div className={classes.modalBody}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  labelText="Email"
                  id={`email-${type}`}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: formData.email,
                    onChange: (e) => {
                      updateFormData("email", e.target.value);
                      updateFormState(
                        "emailState",
                        validateEmail(e.target.value) ? "success" : "error"
                      );
                    },
                  }}
                  success={formStates.emailState === "success"}
                  error={formStates.emailState === "error"}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  labelText="Name"
                  id={`name-${type}`}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: formData.name,
                    onChange: (e) => updateFormData("name", e.target.value),
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={12}>
                <CustomInput
                  labelText="Institution"
                  id={`institution-${type}`}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: formData.institution,
                    onChange: (e) =>
                      updateFormData("institution", e.target.value),
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={12}>
                <CustomInput
                  labelText="About"
                  id={`about-${type}`}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    multiline: true,
                    rows: 4,
                    value: formData.about,
                    onChange: (e) => updateFormData("about", e.target.value),
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={12}>
                <FormControl fullWidth>
                  <InputLabel>User Type</InputLabel>
                  <Select
                    value={formData.typeNumeric}
                    onChange={(e) =>
                      updateFormData("typeNumeric", e.target.value)
                    }
                  >
                    {Object.values(userTypes).map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
          </div>
          <div className={classes.modalFooter}>
            <Button color="transparent" onClick={onClose}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {submitText}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

UserModal.propTypes = {
  type: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    institution: PropTypes.string,
    about: PropTypes.string,
    typeNumeric: PropTypes.string,
  }).isRequired,
  formStates: PropTypes.shape({
    emailState: PropTypes.string,
  }).isRequired,
  updateFormData: PropTypes.func.isRequired,
  updateFormState: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  validateEmail: PropTypes.func.isRequired,
  userTypes: PropTypes.object.isRequired,
};

export default UserModal;

